import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { CartItem, Product } from "@/types";
import { calcDiscount } from "@/lib/format";

const CART_KEY = "amora-belle-sacola";
const FAV_KEY = "amora-belle-favoritos";

interface StoreValue {
  cart: CartItem[];
  cartCount: number;
  subtotal: number;
  discount: number;
  total: number;
  addToCart: (product: Product, colorName: string, size: string, quantity: number) => void;
  updateQuantity: (key: string, quantity: number) => void;
  removeFromCart: (key: string) => void;
  clearCart: () => void;
  favorites: string[];
  toggleFavorite: (product: Product) => void;
  isFavorite: (id: string) => boolean;
  toast: string | null;
  showToast: (message: string) => void;
}

const StoreContext = createContext<StoreValue | null>(null);

function readCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
  } catch {
    return [];
  }
}

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>(() => readCart());
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {
      /* ignore storage errors */
    }
  }, [cart]);

  useEffect(() => {
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(favorites));
    } catch {
      /* ignore storage errors */
    }
  }, [favorites]);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  }, []);

  const addToCart = useCallback(
    (product: Product, colorName: string, size: string, quantity: number) => {
      const color = product.colors.find((item) => item.name === colorName) ?? product.colors[0];
      const unitPrice = product.promoPrice ?? product.price;
      const key = `${product.id}__${color.name}__${size}`;
      setCart((prev) => {
        const existing = prev.find((item) => item.key === key);
        if (existing) {
          return prev.map((item) =>
            item.key === key
              ? { ...item, quantity: Math.min(item.quantity + quantity, item.stock || 99) }
              : item,
          );
        }
        return [
          ...prev,
          {
            key,
            productId: product.id,
            code: product.code,
            name: product.name,
            category: product.category,
            colorName: color.name,
            colorHex: color.hex,
            size,
            quantity: Math.min(quantity, product.stock || 99),
            unitPrice,
            image: color.image,
            stock: product.stock,
          },
        ];
      });
      showToast("Sua peça foi adicionada à sacola.");
    },
    [showToast],
  );

  const updateQuantity = useCallback((key: string, quantity: number) => {
    setCart((prev) =>
      prev.map((item) =>
        item.key === key
          ? { ...item, quantity: Math.max(1, Math.min(quantity, item.stock || 99)) }
          : item,
      ),
    );
  }, []);

  const removeFromCart = useCallback((key: string) => {
    setCart((prev) => prev.filter((item) => item.key !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleFavorite = useCallback(
    (product: Product) => {
      setFavorites((prev) => {
        const exists = prev.includes(product.id);
        showToast(
          exists
            ? "Produto removido dos favoritos."
            : "Produto adicionado aos favoritos.",
        );
        return exists ? prev.filter((id) => id !== product.id) : [...prev, product.id];
      });
    },
    [showToast],
  );

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  const totals = useMemo(() => {
    const count = cart.reduce((sum, item) => sum + item.quantity, 0);
    const subtotal = cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
    const discount = calcDiscount(subtotal);
    return { count, subtotal, discount, total: subtotal - discount };
  }, [cart]);

  const value = useMemo<StoreValue>(
    () => ({
      cart,
      cartCount: totals.count,
      subtotal: totals.subtotal,
      discount: totals.discount,
      total: totals.total,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      favorites,
      toggleFavorite,
      isFavorite,
      toast,
      showToast,
    }),
    [
      cart,
      totals,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      favorites,
      toggleFavorite,
      isFavorite,
      toast,
      showToast,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore precisa estar dentro de StoreProvider.");
  }
  return context;
}